import React from 'react';
import PortalService from '../../../services/portal/portal.service';
import { usePortalResource } from '../usePortalResource';
import PortalSection, { Card, EmptyState } from '../PortalSection';
import { formatPortalDate } from '../format';
import { disciplineLabel } from '../../../constants/disciplines';
import type { PortalCarePlanDoc } from '../../../dtos/portal/portal.dto';

const PlanSections: React.FC<{ plan: PortalCarePlanDoc }> = ({ plan }) => (
  <>
    {plan.sections.map((section, sIndex) => (
      <Card key={`${section.title}-${sIndex}`}>
        <h3 className="mb-2 text-base font-semibold text-slate-900">{section.title}</h3>
        {section.items.length === 0 ? (
          <p className="text-sm text-slate-700">None listed.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-slate-800">
            {section.items.map((item, iIndex) => (
              <li key={iIndex}>
                {item.text}
                {item.detail && <span className="block text-sm text-slate-700">{item.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    ))}
  </>
);

/** Current completed care plan of each discipline (Phase 20B), read-only. */
const CarePlanScreen: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { data, loading, error } = usePortalResource(PortalService.getCarePlan, patientId);
  const plans = data ? (data.carePlans && data.carePlans.length ? data.carePlans : data.carePlan ? [data.carePlan] : []) : [];
  return (
    <PortalSection title="Care plan" loading={loading} error={error}>
      {data &&
        (plans.length === 0 ? (
          <EmptyState>No completed care plan is available yet.</EmptyState>
        ) : (
          <div className="flex flex-col gap-6">
            {plans.map(plan => (
              <section key={plan.carePlanId} className="flex flex-col gap-4" aria-label={`${disciplineLabel(plan.discipline)} care plan`}>
                <h2 className="text-lg font-semibold text-slate-900">
                  {disciplineLabel(plan.discipline)} care plan
                  <span className="block text-sm font-normal text-slate-700">Completed {formatPortalDate(plan.completedAt, false)}</span>
                </h2>
                <PlanSections plan={plan} />
              </section>
            ))}
          </div>
        ))}
    </PortalSection>
  );
};

export default CarePlanScreen;
